import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import RoundCountdown from '../../Calls/RoundCountdown';
import Select from '../../../Select';
import Throbber from '../../../Throbber';

import {
  VOX_STATES,
  VOX_STATES_LABELS,
  VOX_CHOOSABLE_STATES,
} from '../../../../consts/vox';

const {
  StyledSelected: StyledSelectedOption,
  StyledOptions,
  StyledOption: StyledOptionWrapper,
  StyledStatusIcon,
} = Select;

class DialerStatusSelector extends React.PureComponent {
  static generatedOptions() {
    const labels = VOX_STATES_LABELS;
    const options = VOX_CHOOSABLE_STATES;

    return options.map(value => ({
      title: labels[value],
      value,
    }));
  }

  static optionRenderer({ title, value }) {
    return (
      <StyledOption>
        <StyledOptionIndicator status={value} />
        {title}
      </StyledOption>
    );
  }

  static getDerivedStateFromProps(props, state) {
    const { value: nextForceVal } = props;
    const { value: nextVal, manualState } = state;
    const isStateChanged = manualState !== nextVal;
    const isPropChanged = manualState !== nextForceVal;

    if (isStateChanged) {
      return {
        value: nextVal,
        manualState: nextVal,
      };
    }

    if (isPropChanged) {
      return {
        manualState: nextForceVal,
        value: nextForceVal,
      };
    }

    return null;
  }

  constructor(props) {
    super(props);

    this.state = {
      value: props.value,
      manualState: props.value,
    };
    this.handleAfterService = this.handleAfterService.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.headRenderer = this.headRenderer.bind(this);
  }

  handleAfterService() {
    const { onChange, manualState } = this.props;

    this.setState(
      prevState => ({
        manualState: null,
        value: prevState.manualState,
      }),
      () => {
        onChange({
          isManual: true,
          manualState: null,
          state: manualState,
          operatorState: manualState,
        });
      }
    );
  }

  handleChange({ detail }) {
    const { onChange } = this.props;
    const { value } = detail;
    const newData = { value, isManual: true };

    this.setState(newData, () =>
      onChange({
        isManual: true,
        state: value,
        operatorState: value,
      })
    );
  }

  headRenderer() {
    const { value } = this.props;
    const isAfterService = value === VOX_STATES.AFTER_SERVICE;
    return (
      <StyledHead>
        <StyledOption>{VOX_STATES_LABELS[value]}</StyledOption>
        {!isAfterService ? (
          <StyledStatusIndicator status={value} />
        ) : (
          <StyledRoundCountdown seconds={25} onStop={this.handleAfterService} />
        )}
      </StyledHead>
    );
  }

  render() {
    const { value, disabled } = this.props;

    const options = DialerStatusSelector.generatedOptions();

    if (disabled) {
      return (
        <StyledHead>
          <StyledOption>
            <StyledThrobber size={10} color={'#ffffff'} />
          </StyledOption>
        </StyledHead>
      );
    }

    return (
      <StyledDialerStatusSelector
        {...this.props}
        key={value}
        headRenderer={this.headRenderer}
        optionRenderer={DialerStatusSelector.optionRenderer}
        options={options}
        value={value}
        optionHeight={46}
        onChange={this.handleChange}
      />
    );
  }
}

DialerStatusSelector.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  manualState: PropTypes.oneOf(Object.values(VOX_STATES)).isRequired,
  value: PropTypes.oneOf(Object.values(VOX_STATES)).isRequired,
};

DialerStatusSelector.defaultProps = {
  className: '',
  disabled: false,
  onChange: Function.prototype,
};

const StyledDialerStatusSelector = styled(Select)`
  width: auto;
  color: white;
  font-size: 12px;
  position: relative;
  line-height: normal;
  background-color: transparent;

  ${StyledSelectedOption} {
    padding: 0;
    opacity: 1;
  }

  ${StyledOptions} {
    right: 0;
    overflow: visible;
    margin-top: 10px;
    position: absolute;
    margin-right: 20px;

    &:before {
      left: auto;
      right: 15px;
    }
  }

  ${StyledOptionWrapper} {
    color: black;
    min-height: 39px;
    line-height: 39px;
    background-color: transparent;

    &[data-selected='true'] {
      background-color: #dcedff;
    }
  }

  ${StyledStatusIcon} {
    display: none;
  }

  ${p =>
    (p.value === VOX_STATES.READY || p.value === VOX_STATES.IN_SERVICE) &&
    `
    ${StyledOptions}:before {
      border-bottom-color: #dcedff;
    }
  `}
`;

const StyledHead = styled.div`
  display: flex;
  align-items: center;
`;

const StyledThrobber = styled(Throbber)`
  margin-right: 10px;
  position: relative;
`;

const StyledOption = styled.div`
  display: flex;
  white-space: nowrap;
  padding-right: 10px;
  font-size: 14px;
  align-items: center;
`;

const StyledIndicator = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;

  ${p =>
    p.status === VOX_STATES.DND &&
    `
    background-image: linear-gradient(to top, #e5200e, #ff860c);
  `}

  ${p =>
    (p.status === VOX_STATES.OFFLINE ||
      p.status === VOX_STATES.AFTER_SERVICE) &&
    `
    background-color: #c2c5cc;
  `}
  
  ${p =>
    p.status === VOX_STATES.ONLINE &&
    `
    background-image: linear-gradient(to top, #f7aa1b, #faed61);
  `}
  
  ${p =>
    (p.status === VOX_STATES.READY || p.status === VOX_STATES.IN_SERVICE) &&
    `
    background-image: linear-gradient(to top, #00b0b6, #00e04b);
  `}
`;

const StyledStatusIndicator = styled(StyledIndicator)`
  margin-top: -2px;
`;

const StyledOptionIndicator = styled(StyledIndicator)`
  margin-right: 10px;
`;

const StyledRoundCountdown = styled(RoundCountdown)`
  margin-left: 2px;
  margin-top: 1px;
`;

export default DialerStatusSelector;
